/**
 * PropertyMeta — shared deal-row meta primitives.
 *
 * Single source of truth for the two-line strip that hangs under the
 * property address on every deal-listing surface:
 *
 *   Line 1: Detail  → "Single Family · 3 bd · 2 ba · 1,200 sqft · $2,000/mo · Miami, FL"
 *   Line 2: Description (italic, line-clamp-2) → user-provided OR auto-generated
 *
 * Surfaces consuming this file:
 *   • Portfolio.jsx           (table + mobile cards) — both lines
 *   • Dashboard.jsx           (Recent deals)         — both lines
 *   • DealDetail.jsx          (page header)          — both lines
 *   • Pipeline.jsx            (Kanban cards)         — detail line only
 *   • CompareToModal.jsx      (picker)               — detail line only
 *   • CommandPalette.jsx      (recent deals)         — compact string helper
 *
 * Extracted from Portfolio.jsx (commit b8436d0) — behaviour preserved
 * verbatim there. The auto-generated description format and the "~"
 * prefix + tooltip convention were established in X2 and stay the same.
 */
import { propertyTypeLabel } from "@/lib/format";
import { flagEmoji, countryOf } from "@/lib/flags";

// Tight integer-or-trimmed-decimal formatter for bedrooms / bathrooms.
// `2.0` → `2`, `2.5` → `2.5`, anything else falsy → null.
function bdBa(n) {
  if (n == null || Number(n) <= 0) return null;
  const x = Number(n);
  return Number.isInteger(x) ? String(x) : x.toFixed(1).replace(/\.0$/, "");
}

// Strategy → human label. Default to long-term to match the wizard.
function strategyLabel(s) {
  if (s === "short_term") return "Short-term rental";
  if (s === "mid_term") return "Mid-term rental";
  return "Long-term rental";
}

// ─── PropertyDetailLine ─────────────────────────────────────────────────
//
// Joins non-empty pieces with " · " so any missing field is silently
// dropped — no "N/A" placeholders, no broken dots. Truncates with the
// class passed in (callers use `truncate` for tables, `line-clamp-1` for
// cards). When NO pieces resolve, the component renders nothing.
//
// `dense=true` drops the long-form pieces (sqft, rent) for narrow
// surfaces like the Compare picker modal — keeps the strip readable
// inside ~340px columns.
export function PropertyDetailLine({ d, fmt, className = "", dense = false }) {
  const p = d?.property || {};
  const parts = [];
  if (p.property_type) parts.push(propertyTypeLabel(p.property_type));
  const bd = bdBa(p.bedrooms);
  if (bd) parts.push(`${bd} bd`);
  const ba = bdBa(p.bathrooms);
  if (ba) parts.push(`${ba} ba`);
  if (!dense && p.square_feet && Number(p.square_feet) > 0) {
    parts.push(`${Number(p.square_feet).toLocaleString("en-US")} sqft`);
  }
  if (!dense) {
    const rent = Number(d?.monthly_rent) > 0 ? d.monthly_rent : null;
    if (rent && fmt) parts.push(`${fmt.money(rent)}/mo`);
  }
  // Phase 36 Batch R — prepend country flag emoji to the location
  // segment. Region falls back to the legacy `state` field on
  // pre-migration data; country falls back to the legacy `country`.
  const flag = flagEmoji(countryOf(d));
  const region = p.region_code || p.state || "";
  let locSegment = "";
  if (p.city && region) locSegment = `${p.city}, ${region}`;
  else if (p.city) locSegment = p.city;
  else if (region) locSegment = region;
  if (locSegment) parts.push(`${flag} ${locSegment}`);
  if (parts.length === 0) return null;
  return (
    <p
      data-testid={`prow-detail-${d?.id || "x"}`}
      className={`mt-0.5 truncate text-xs text-slate-500 ${className}`}
    >
      {parts.join(" · ")}
    </p>
  );
}

// Plain-string version of the detail line for surfaces that can't take
// JSX (CommandPalette's `hint` slot, ARIA labels, exports). Returns ""
// when no pieces resolve.
export function propertyDetailLineCompact(d, fmt, { dense = true } = {}) {
  const p = d?.property || {};
  const parts = [];
  if (p.property_type) parts.push(propertyTypeLabel(p.property_type));
  const bd = bdBa(p.bedrooms);
  if (bd) parts.push(`${bd} bd`);
  const ba = bdBa(p.bathrooms);
  if (ba) parts.push(`${ba} ba`);
  if (!dense && p.square_feet && Number(p.square_feet) > 0) {
    parts.push(`${Number(p.square_feet).toLocaleString("en-US")} sqft`);
  }
  if (!dense) {
    const rent = Number(d?.monthly_rent) > 0 ? d.monthly_rent : null;
    if (rent && fmt) parts.push(`${fmt.money(rent)}/mo`);
  }
  const flag = flagEmoji(countryOf(d));
  const region = p.region_code || p.state || "";
  let locSegment = "";
  if (p.city && region) locSegment = `${p.city}, ${region}`;
  else if (p.city) locSegment = p.city;
  else if (region) locSegment = region;
  if (locSegment) parts.push(`${flag} ${locSegment}`);
  return parts.join(" · ");
}

// ─── Auto-description ───────────────────────────────────────────────────
//
// Generates a one-line summary from existing fields when a user hasn't
// written their own description yet. Drops missing pieces silently —
// never emits "undefined" / "null". Returns null if there isn't enough
// data to form even a partial sentence.
function autoDescription(d, fmt) {
  const p = d?.property || {};
  const bd = bdBa(p.bedrooms);
  const type = p.property_type ? propertyTypeLabel(p.property_type) : null;
  const loc = p.city && p.state
    ? `${p.city}, ${p.state}`
    : (p.city || p.state || null);
  const rent = Number(d?.monthly_rent) > 0 ? d.monthly_rent : null;
  const strategy = strategyLabel(d?.rental_strategy);
  const head = [bd ? `${bd}-bed` : null, type].filter(Boolean).join(" ");
  const where = loc ? ` in ${loc}` : "";
  const projection = rent && fmt ? ` ${strategy} projected at ${fmt.money(rent)}/month.` : "";
  const sentence = head ? `${head}${where}.${projection}` : null;
  return sentence ? sentence.trim() : null;
}

// ─── PropertyDescription ────────────────────────────────────────────────
//
// Prefers the user-provided `d.description` (italic, no prefix).
// Falls back to autoDescription() prefixed with "~ " (rendered in a
// not-italic, lighter shade) and a title tooltip explaining the prefix.
// Renders nothing when neither path produces text.
export function PropertyDescription({ d, fmt, className = "" }) {
  const user = (d?.description || "").trim();
  const text = user || autoDescription(d, fmt);
  if (!text) return null;
  const isAuto = !user;
  return (
    <p
      data-testid={`prow-description-${d?.id || "x"}`}
      data-auto={isAuto ? "true" : "false"}
      title={isAuto
        ? "Auto-generated — edit the property to add your own description."
        : text}
      className={`mt-1 line-clamp-2 text-xs italic text-slate-500 ${className}`}
    >
      {isAuto ? (
        <span className="not-italic text-slate-400" aria-hidden="true">~ </span>
      ) : null}
      {text}
    </p>
  );
}

// ─── PropertyContactLine ────────────────────────────────────────────────
// Phase 36. One-line contact strip rendered under the description on
// list rows where there's room (Portfolio, Dashboard "Recent deals").
// Skipped on dense surfaces (Kanban / Compare modal) per the brief.
//
// Renders nothing when there's no contact name AND no phone — empty
// rows just collapse, no whitespace gap.
//
// Examples:
//   "📋 Realtor: Maria Hernandez · (305) 555-1234"
//   "🏠 FSBO: John Smith · (305) 555-1234"
//
// Phone is rendered as <a href="tel:..."> so a single tap places the
// call on mobile. Click events are stopPropagation'd so tapping the
// phone doesn't also navigate the parent row link.
function _shortPhoneDisplay(raw) {
  if (!raw) return "";
  const s = String(raw).trim();
  const digits = s.replace(/\D/g, "");
  if (s.startsWith("+1") && digits.length === 11) {
    return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  return s.startsWith("+") ? `+${digits}` : digits || s;
}

const _CONTACT_PREFIX = {
  realtor: { label: "Realtor", icon: "📋" },
  for_sale_by_owner: { label: "FSBO", icon: "🏠" },
  wholesaler: { label: "Wholesaler", icon: "🤝" },
  off_market: { label: "Off-market", icon: "🔒" },
};

export function PropertyContactLine({ d, className = "" }) {
  const lt = d?.listing_type || "unknown";
  const name = (d?.contact_name || "").trim();
  const phone = (d?.contact_phone || "").trim();
  if (!name && !phone) return null;
  const meta = _CONTACT_PREFIX[lt];
  const prefix = meta
    ? `${meta.icon} ${meta.label}: `
    : ""; // listing_type=unknown → no prefix, just "Name · phone"
  const phoneDisplay = phone ? _shortPhoneDisplay(phone) : "";
  const telHref = phone
    ? (phone.startsWith("+") ? `tel:+${phone.replace(/\D/g, "")}` : `tel:${phone.replace(/\D/g, "")}`)
    : null;
  return (
    <p
      data-testid={`prow-contact-${d?.id || "x"}`}
      data-listing-type={lt}
      className={`mt-1 truncate text-xs text-slate-500 ${className}`}
    >
      <span>{prefix}</span>
      {name ? <span className="text-slate-600">{name}</span> : null}
      {name && phoneDisplay ? <span> · </span> : null}
      {phoneDisplay ? (
        <a
          href={telHref}
          onClick={(e) => e.stopPropagation()}
          className="hover:text-indigo-600 hover:underline"
          data-testid={`prow-contact-phone-${d?.id || "x"}`}
        >
          {phoneDisplay}
        </a>
      ) : null}
    </p>
  );
}

// ─── detectListingPlatform ──────────────────────────────────────────────
//
// Phase 36 polish. Reads `url` and returns { label, badgeClass } for the
// platform chip rendered next to "View listing →" on Deal Detail and as
// a "Detected: …" feedback chip on the wizard. Unknown domains fall
// back to the bare hostname so MLS / custom domains still get a sensible
// label.
//
// badgeClass is a small Tailwind pair (bg + text) tuned to the brand
// colour of each platform — purely decorative; safe to drop.
const _PLATFORMS = [
  { match: (h) => h.includes("zillow.com"),      label: "Zillow",         badgeClass: "bg-blue-50 text-blue-700 ring-blue-200" },
  { match: (h) => h.includes("realtor.com"),     label: "Realtor.com",    badgeClass: "bg-red-50 text-red-700 ring-red-200" },
  { match: (h) => h.includes("redfin.com"),      label: "Redfin",         badgeClass: "bg-purple-50 text-purple-700 ring-purple-200" },
  { match: (h) => h.includes("trulia.com"),      label: "Trulia",         badgeClass: "bg-emerald-50 text-emerald-700 ring-emerald-200" },
  { match: (h) => h.includes("homes.com"),       label: "Homes.com",      badgeClass: "bg-amber-50 text-amber-700 ring-amber-200" },
  { match: (h) => h.includes("loopnet.com"),     label: "LoopNet",        badgeClass: "bg-slate-100 text-slate-700 ring-slate-200" },
  { match: (h) => h.includes("crexi.com"),       label: "Crexi",          badgeClass: "bg-slate-100 text-slate-700 ring-slate-200" },
  { match: (h) => h.includes("craigslist.org"),  label: "Craigslist",     badgeClass: "bg-violet-50 text-violet-700 ring-violet-200" },
  { match: (h, p) => h.includes("facebook.com") && p.includes("/marketplace"),
                                                  label: "FB Marketplace", badgeClass: "bg-blue-50 text-blue-700 ring-blue-200" },
];

export function detectListingPlatform(url) {
  if (!url) return null;
  let host = "";
  let path = "";
  try {
    const u = new URL(String(url).trim());
    host = (u.hostname || "").toLowerCase();
    path = (u.pathname || "").toLowerCase();
  } catch {
    return null;
  }
  if (!host) return null;
  for (const p of _PLATFORMS) {
    if (p.match(host, path)) return { label: p.label, badgeClass: p.badgeClass };
  }
  // Unknown domain — fall back to the bare hostname (strip leading www.).
  return {
    label: host.replace(/^www\./, ""),
    badgeClass: "bg-slate-100 text-slate-600 ring-slate-200",
  };
}

